import { List, ListItem, styled } from "@mui/material";
import colors from "../../theme/themeConstants";
import { useNavigate } from "react-router-dom";
import { hideAllMenus, showAuthModal } from "store/application/application-action";

import ProfileMenuIcons from "./ProfileMenuIcons";
import { ReactComponent as RightArrow } from "components/icons/right_arrow.svg";
import { routes } from "routes";
import { logout } from "../../services/Auth";
import { ProfileMenuHeader } from "../profile/ProfileMenuHeader";

import "./ProfileMenu.scss";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { fetchCurrentUserSuccess } from "store/user/user-action";

interface IMenuItem {
  text: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  url: string;
  verified: boolean;
}

const SAVED_PAGE = "#saved";
const PROFILE_PAGE = "#profile";
const LOGOUT_PAGE = "#logout";

const menuItems: IMenuItem[] = [
  {
    text: "My Profile",
    Icon: ProfileMenuIcons.MyProfileIcon,
    url: PROFILE_PAGE,
    verified: false,
  },
  {
    text: "My Dashboard",
    Icon: ProfileMenuIcons.DashboardIcon,
    url: routes.dashboard(),
    verified: true,
  },
  {
    text: "Manage",
    Icon: ProfileMenuIcons.ManageIcon,
    url: routes.manageProfile(),
    verified: false,
  },
  {
    text: "Following",
    Icon: ProfileMenuIcons.FollowingIcon,
    url: routes.following(),
    verified: false,
  },
  {
    text: "Followers",
    Icon: ProfileMenuIcons.FollowingIcon,
    url: routes.followers(),
    verified: false,
  },
  {
    text: "My Saved",
    Icon: ProfileMenuIcons.SavedContentIcon,
    url: SAVED_PAGE,
    verified: false,
  },
  {
    text: "My Niches",
    Icon: ProfileMenuIcons.MyNichesIcon,
    url: routes.myNiches(),
    verified: false,
  },
  {
    text: "Settings",
    Icon: ProfileMenuIcons.SettingsIcon,
    url: routes.accountSettings(),
    verified: false,
  },
  {
    text: "Log in",
    Icon: ProfileMenuIcons.LogoutIcon,
    url: "/login",
    verified: false,
  },
  {
    text: "Log out",
    Icon: ProfileMenuIcons.LogoutIcon,
    url: LOGOUT_PAGE,
    verified: false,
  },
];

const ProfileMenu = ({ isProfileMenuOpen }: { isProfileMenuOpen: boolean }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: IAppState) => state.user.currentUser);
  const isLoggedIn = !!Object.entries(user).length;

  const navigate = useNavigate();

  const handleMenuItemClick = (url: string) => async () => {
    dispatch(hideAllMenus());

    if (!isLoggedIn) {
      dispatch(showAuthModal());
      return;
    }

    if (url === PROFILE_PAGE) {
      navigate(routes.user(user.username));
    } else if (url === SAVED_PAGE) {
      navigate(routes.userCollections(user.username));
    } else if (url === "/createnewniche") {
      navigate(routes.myNiches(), { state: { createNicheInitiallyOpen: true } });
    } else if (url === LOGOUT_PAGE) {
      await logout();
      dispatch(fetchCurrentUserSuccess(null));
      navigate("/");
    } else {
      navigate(url);
    }
  };

  const canShow = (item: IMenuItem): boolean => {
    if (item.text === "Log in") {
      return !isLoggedIn;
    }

    if (item.text === "Log out") {
      return isLoggedIn;
    }

    return true;
  };

  const visibleItems = menuItems.filter((item): boolean => {
    return canShow(item);
  });

  return (
    <Container
      className="profileMenu"
      isProfileMenuOpen={isProfileMenuOpen}
    >
      <div className="profileHeader">
        {user && <ProfileMenuHeader
          user={user}
        />}
      </div>

      <List className="itemList">
        {visibleItems.map(({ url, Icon, text }, index: number) => (
          <ListItem
            key={index}
            className="listItem"
            button
            onClick={handleMenuItemClick(url.toString())}
          >
            <div>
              <Icon />
              <span className="text">{text}</span>
            </div>
            <RightArrow className="right" />
          </ListItem>
        ))}
      </List>
    </Container >
  );
};
export default ProfileMenu;

const Container = styled("div",
  {
    shouldForwardProp: (prop) => prop !== "isProfileMenuOpen"
  })<
    { isProfileMenuOpen: boolean }>(({ isProfileMenuOpen }) => ({
      display: isProfileMenuOpen ? "block" : "none",
      position: "fixed",
      backgroundColor: colors.backgroundBlack,
      right: isProfileMenuOpen ? "0" : "74rem",
    }));
